import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const resultBadge = (r) => {
  if (r === "W") return <span className="text-[10px] uppercase tracking-wider bg-[#22C55E] text-black font-bold px-1.5 py-0.5 rounded-sm">W</span>;
  if (r === "L") return <span className="text-[10px] uppercase tracking-wider bg-[#FF3B30] text-black font-bold px-1.5 py-0.5 rounded-sm">L</span>;
  if (r === "TIE") return <span className="text-[10px] uppercase tracking-wider bg-zinc-500 text-black font-bold px-1.5 py-0.5 rounded-sm">T</span>;
  return <span className="text-[10px] uppercase tracking-wider text-[#007AFF] border border-[#007AFF]/40 px-1.5 py-0.5 rounded-sm">Pending</span>;
};

export default function Receipts() {
  const { user } = useAuth();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Access gate: receipts contain the team names + locked-at timestamps of
  // upcoming picks — that's the strategy's full reveal. Only paying subscribers,
  // active trials, and admins should see it.
  const fullAccess = useMemo(() => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.subscription_status === "trial" || user.subscription_status === "active") return true;
    if (user.trial_until) {
      try {
        return new Date(user.trial_until) > new Date();
      } catch { return false; }
    }
    return false;
  }, [user]);

  useEffect(() => {
    if (!fullAccess) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/picks/locked");
        if (!cancelled) setPicks(data.picks || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load receipts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fullAccess]);

  const grouped = useMemo(() => {
    const m = {};
    for (const p of picks) {
      const dt = p.target_date || "Unknown";
      (m[dt] = m[dt] || []).push(p);
    }
    return Object.keys(m).sort().reverse().map((dt) => ({ date: dt, picks: m[dt] }));
  }, [picks]);

  // Mon → Sun week boundaries (local time). Receipts KPIs scope to this week
  // ONLY; the running all-time totals live on the public landing.
  const weekBounds = useMemo(() => {
    const now = new Date();
    const day = now.getDay(); // Sun=0, Mon=1, ...
    const offsetToMon = (day === 0 ? 6 : day - 1);
    const mon = new Date(now);
    mon.setHours(0, 0, 0, 0);
    mon.setDate(now.getDate() - offsetToMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    sun.setHours(23, 59, 59, 999);
    const toIso = (d) => d.toISOString().slice(0, 10);
    return { startIso: toIso(mon), endIso: toIso(sun), monLabel: mon, sunLabel: sun };
  }, []);

  const inThisWeek = (iso) => iso && iso >= weekBounds.startIso && iso <= weekBounds.endIso;

  const stats = useMemo(() => {
    const wkPicks = picks.filter((p) => inThisWeek(p.target_date));
    const settled = wkPicks.filter((p) => p.result === "W" || p.result === "L");
    const wins = settled.filter((p) => p.result === "W").length;
    const losses = settled.length - wins;
    const pending = wkPicks.filter((p) => p.result === "PENDING").length;
    return {
      total: wkPicks.length,
      settled: settled.length,
      wins, losses, pending,
      hit_rate: settled.length ? (wins / settled.length) : 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, weekBounds]);

  const weekLabel = useMemo(() => {
    const f = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${f(weekBounds.monLabel)} – ${f(weekBounds.sunLabel)}`;
  }, [weekBounds]);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="receipts-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white mb-6" data-testid="receipts-back">
        <ArrowLeft size={12} /> Back to picks
      </Link>

      {!fullAccess && (
        <div className="bg-[#121212] border border-[#007AFF]/30 rounded-sm p-10 text-center" data-testid="receipts-paywall">
          <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-3">
            <Lock size={14} weight="fill" /> Subscribers only
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-3">RECEIPTS ARE LOCKED.</h1>
          <p className="text-sm text-zinc-400 max-w-xl mx-auto mb-6 leading-relaxed">
            Receipts reveal every team Charlie and Dakota picked — including picks for upcoming games.
            That&apos;s the whole strategy. Start your 7-day free trial to see them.
          </p>
          <Link
            to="/signup"
            data-testid="receipts-signup-cta"
            className="inline-block px-6 py-3 bg-[#22C55E] hover:bg-[#16A34A] text-black text-xs uppercase tracking-[0.25em] font-bold rounded-sm transition-colors"
          >
            Start 7-day free trial →
          </Link>
          <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-600">
            Already a subscriber? <Link to="/login" className="text-[#007AFF] hover:underline">Sign in</Link>
          </div>
        </div>
      )}

      {fullAccess && (
        <>
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">
          <ShieldCheck size={14} weight="fill" /> Receipts · Tamper-evident
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none">PROOF.</h1>
        <p className="text-sm text-zinc-400 mt-4 max-w-3xl leading-relaxed">
          Every pick on this page was <span className="text-white font-bold">locked into our database BEFORE first pitch</span>.
          We can&apos;t edit them, hide them, or pretend we didn&apos;t make a bad call. The
          timestamp next to each pick is the moment we committed — the result column is what
          MLB&apos;s official scoreboard says happened.
        </p>
      </div>

      {/* KPI strip — scoped to current Mon→Sun week */}
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">This week&apos;s performance</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-d" data-testid="receipts-week-label">
          Week of {weekLabel}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid="receipt-stat-total">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Picks This Week</div>
          <div className="font-display text-3xl mt-1">{stats.total}</div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid="receipt-stat-record">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Week Record</div>
          <div className="font-display text-3xl mt-1">{stats.wins}-{stats.losses}</div>
        </div>
        <div className="bg-[#121212] border border-[#22C55E]/30 rounded-sm p-4" data-testid="receipt-stat-hit-rate">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Week Hit Rate</div>
          <div className="font-display text-3xl mt-1 text-[#22C55E]">
            {stats.settled ? (stats.hit_rate * 100).toFixed(1) + "%" : "—"}
          </div>
        </div>
        <div className="bg-[#121212] border border-[#007AFF]/30 rounded-sm p-4" data-testid="receipt-stat-pending">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pending This Week</div>
          <div className="font-display text-3xl mt-1 text-[#007AFF]">{stats.pending}</div>
        </div>
      </div>

      {loading && (
        <div className="text-zinc-500 font-mono-d" data-testid="receipts-loading">Loading receipts…</div>
      )}
      {error && (
        <div className="text-[#FF3B30] font-mono-d" data-testid="receipts-error">{error}</div>
      )}

      {!loading && !error && picks.length === 0 && (
        <div className="bg-[#121212] border border-dashed border-white/10 rounded-sm p-10 text-center text-sm text-zinc-400" data-testid="receipts-empty">
          No locked picks yet. The first daily lock job runs at 10 PM PT.
        </div>
      )}

      {!loading && !error && grouped.length > 0 && (
        <div className="space-y-8">
          {grouped.map((g) => (
            <div key={g.date} data-testid={`receipts-group-${g.date}`}>
              <div className="flex items-baseline gap-3 mb-3 pb-2 border-b border-white/10">
                <div className="font-display text-2xl tracking-wider">{fmtDate(g.date)}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono-d">{g.date}</div>
                <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
                  {g.picks.length} {g.picks.length === 1 ? "pick" : "picks"}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                    <tr>
                      <th className="text-left py-2 pr-3">Analyst</th>
                      <th className="text-left py-2 pr-3">Pick</th>
                      <th className="text-left py-2 pr-3">Opponent</th>
                      <th className="text-left py-2 pr-3">Locked At</th>
                      <th className="text-left py-2 pr-3">Final</th>
                      <th className="text-left py-2 pr-3">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.picks.map((p) => (
                      <tr key={p.lock_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`receipt-row-${p.lock_id}`}>
                        <td className="py-2.5 pr-3">
                          <span className={`text-xs font-bold ${p.analyst === "Charlie" ? "text-[#22C55E]" : "text-yellow-400"}`}>
                            {p.analyst}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3">
                          <div className="text-sm font-bold">{p.team}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.team_abbr}</div>
                        </td>
                        <td className="py-2.5 pr-3 font-mono-d text-xs">
                          {p.is_home === true ? (
                            <><span className="text-zinc-500">{p.opponent_abbr || p.opponent} @ </span><span className="text-white font-bold">{p.team_abbr}</span></>
                          ) : p.is_home === false ? (
                            <><span className="text-white font-bold">{p.team_abbr}</span><span className="text-zinc-500"> @ {p.opponent_abbr || p.opponent}</span></>
                          ) : (
                            <span className="text-zinc-400">vs {p.opponent}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs font-mono-d text-zinc-400">
                          <span className="inline-flex items-center gap-1">
                            <Lock size={10} weight="fill" className="text-[#22C55E]" />
                            {fmtTime(p.locked_at)}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 text-xs font-mono-d text-zinc-300">
                          {p.final_score
                            ? `${p.final_score.team}-${p.final_score.opponent}`
                            : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="py-2.5 pr-3">{resultBadge(p.result)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-white/10 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
        Public API · <code className="text-zinc-500 normal-case tracking-normal font-mono-d">GET /api/picks/locked</code> · Verifiable
      </div>
        </>
      )}
    </div>
  );
}
